<template>
  <div class="size-range">
    <div v-for="side in sides" :key="side.key" class="size-range-side">
      <span class="size-range-label">{{ side.label }} ({{ unit }})</span>
      <span class="size-range-inputs">
        <input v-model="local[side.min]" type="number" min="0" inputmode="decimal" class="size-range-input"
          placeholder="from" :aria-label="`${side.label} from, in ${unit}`" @input="queueEmit" @blur="flush">
        <span aria-hidden="true">-</span>
        <input v-model="local[side.max]" type="number" min="0" inputmode="decimal" class="size-range-input"
          placeholder="to" :aria-label="`${side.label} to, in ${unit}`" @input="queueEmit" @blur="flush">
      </span>
    </div>
  </div>
</template>

<script>
import { EMPTY_SIZE_RANGE } from '@/utils/sizeUnits'
import { SIZE_RANGE_SIDES } from '@/data/sizeBrackets'

// Wait for a pause in typing so results don't jump on every digit
const EMIT_DELAY = 500

export default {
  name: 'SizeRangeInputs',

  props: {
    modelValue: { type: Object, default: () => ({ ...EMPTY_SIZE_RANGE }) },
    unit: { type: String, default: 'cm' }
  },

  emits: ['update:modelValue'],

  setup() {
    return { sides: SIZE_RANGE_SIDES }
  },

  data() {
    return {
      local: { ...EMPTY_SIZE_RANGE, ...this.modelValue }
    }
  },

  watch: {
    // Parent-driven clears (a bracket picked, Clear clicked)
    modelValue(val) {
      this.local = { ...EMPTY_SIZE_RANGE, ...val }
    }
  },

  beforeUnmount() {
    clearTimeout(this.emitTimer)
  },

  methods: {
    queueEmit() {
      clearTimeout(this.emitTimer)
      this.emitTimer = setTimeout(this.emitNow, EMIT_DELAY)
    },
    // Leaving a box applies what was typed at once; it runs before the click that moved focus (a bracket, Clear)
    flush() {
      if (this.emitTimer) this.emitNow()
    },
    emitNow() {
      clearTimeout(this.emitTimer)
      this.emitTimer = null
      this.$emit('update:modelValue', { ...this.local })
    }
  }
}
</script>

<style scoped>
/* Inside the size dropdown: one side per line, boxes sharing the row */
.size-range {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem 0.85rem 0.75rem;
  font-size: 0.875rem;
  text-transform: lowercase;
}

.size-range-side {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.size-range-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.size-range-input {
  flex: 1 1 0;
  min-width: 0;
  padding: 0.25rem 0.5rem;
  border: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-text) 20%, var(--color-bg));
  border-radius: 8px;
  background: var(--color-bg);
  color: var(--color-text);
  font: inherit;
  line-height: 1.4;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-highlight);
  }

  &::placeholder {
    color: color-mix(in srgb, var(--color-text) 60%, var(--color-bg));
  }
}
</style>
